import React, { useMemo, useState } from "react";
import {
	CCAvatar,
	CCButton,
	CCField,
	CCModal,
	CCMultiSelect,
	CCSpinner,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { TypedLottieSpinner } from "pymble-ui/components";
import {
	ArrowDown,
	ArrowUp,
	Plus,
	SquarePen,
	Trash2,
	TriangleAlert,
	X,
} from "lucide-react";
import {
	PublishRoleHolder,
	PublishChainTemplate,
	refusalDetail,
	usePublishChains,
	usePublishRoles,
} from "../hooks/useEventPublish";
import { useEventTypes } from "../hooks/useEventTypes";
import { Tooltip } from "../components/common/Tooltip";
import { useProfileImages } from "../hooks/useProfileImages";
import { personName } from "../utils/personName";
import "../components/eventManager/eventManager.css";

/** A chain with nothing in it yet, for the Add chain button. */
/** How many faces a step shows before the rest become a "+n". */
const MAX_FACES = 3;

/**
 * Who a step is waiting on, in words.
 *
 * The faces beside this say how many; the line says who, and stops naming them
 * after the first — three full names in a 320px card wrap into three lines and
 * bury the role underneath.
 */
const holderLine = (holders: PublishRoleHolder[], role: string) => {
	if (holders.length === 0) return role;
	const first = personName(holders[0].DelegateName || holders[0].Name) || role;
	if (holders.length === 1) return first;
	const rest = holders.length - 1;
	return `${first} and ${rest} other${rest > 1 ? "s" : ""}`;
};

const blankChain = (): PublishChainTemplate => ({
	Code: "",
	Name: "",
	AppliesTo: { EventTypes: [] },
	Steps: [],
});

/** Codes are keys, so they are constrained rather than free text. */
const toCode = (value: string) =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 40);

/**
 * Workflow administration — which roles sign an event off, and for which kinds
 * of event. The Workflows tab of the approval administration screen.
 *
 * The sibling of the roles screen: that one says who holds a role, this one says
 * where a role appears in the process. Both are `event_publish_admin`, and both
 * refuse before rendering rather than letting every control fail at the API.
 *
 * A chain is edited and saved WHOLE. Steps are an order, and patching one at a
 * time would let two administrators reordering at once produce a chain neither
 * of them chose. The draft lives in local state until Save, so a half-finished
 * reorder is never what the next submission runs.
 *
 * EDITING A CHAIN AFFECTS THE NEXT SUBMISSION, not the ones already running: an
 * event snapshots its steps when it is submitted. Events currently in review on
 * the chain are dropped back to `changes_requested` by the API so they re-run
 * against the new order — the same rule as a material edit to an event — and the
 * confirm line says so, because that is somebody's approval being undone.
 */
export const WorkflowsAdmin: React.FC = () => {
	const toast = useToast();
	const {
		chains,
		chainsLoading,
		chainsError,
		saveChain,
		createChain,
		deleteChain,
		chainsSaving,
	} = usePublishChains();
	const { roles } = usePublishRoles();
	const { imageUrl } = useProfileImages();

	const { types: eventTypes, loaded: typesLoaded } = useEventTypes();

	/** The chain being edited, as a draft. Null is "nothing open". */
	const [draft, setDraft] = useState<PublishChainTemplate | null>(null);
	const [isNew, setIsNew] = useState(false);

	/** Every live holder of each role — anyone who could take the step. */
	const roleHolders = useMemo(() => {
		const map = new Map<string, PublishRoleHolder[]>();
		for (const role of roles) {
			map.set(
				role.Code,
				(role.Holders ?? []).filter((h) => h.Live),
			);
		}
		return map;
	}, [roles]);

	/** Roles worked out per event, which have no holders by design. */
	const derivedRoles = useMemo(
		() => new Set(roles.filter((r) => r.Derived).map((r) => r.Code)),
		[roles],
	);

	const roleName = useMemo(() => {
		const map = new Map<string, string>();
		for (const role of roles) map.set(role.Code, role.Name || role.Code);
		return map;
	}, [roles]);

	/**
	 * Event types already spoken for by another workflow. The API refuses a
	 * second workflow claiming the same type, so those options are disabled with
	 * the owner named, rather than resolved by array order nobody can see.
	 */
	const claimedTypes = useMemo(() => {
		const types = new Map<string, string>();
		for (const chain of chains) {
			if (draft && chain.Code === draft.Code) continue;
			const label = chain.Name || chain.Code;
			for (const t of chain.AppliesTo?.EventTypes ?? []) types.set(t, label);
		}
		return types;
	}, [chains, draft]);

	const edit = (chain: PublishChainTemplate) => {
		setIsNew(false);
		// A copy, so Cancel really cancels.
		setDraft(JSON.parse(JSON.stringify(chain)) as PublishChainTemplate);
	};

	const patch = (change: Partial<PublishChainTemplate>) =>
		setDraft((prev) => (prev ? { ...prev, ...change } : prev));

	/**
	 * The type options: the catalogue, plus anything this workflow already
	 * holds that is not in it.
	 *
	 * The API adopts old documents on read, moving calendar labels out of
	 * `EventTypes`, but a value that is neither — a retired type, or one written
	 * before the catalogue was seeded — stays put. Without a tick box it could
	 * not be removed and would be sent back on the next save for the API to
	 * refuse as uncatalogued.
	 */
	const typeOptions = useMemo(() => {
		const known = eventTypes.map((t) => t.Code);
		const stored = draft?.AppliesTo?.EventTypes ?? [];
		return [...known, ...stored.filter((t) => !known.includes(t))];
	}, [eventTypes, draft]);

	const typeLabel = useMemo(() => {
		const map = new Map<string, string>();
		for (const t of eventTypes) map.set(t.Code, t.Name ?? t.Code);
		return map;
	}, [eventTypes]);

	const setTypes = (values: string[]) => {
		if (!draft) return;
		patch({ AppliesTo: { ...draft.AppliesTo, EventTypes: values } });
	};

	const addStep = (roleId: string) => {
		if (!draft || !roleId) return;
		patch({
			Steps: [
				...draft.Steps,
				{
					Order: draft.Steps.length,
					RoleId: roleId,
					Name: roleName.get(roleId) ?? roleId,
				},
			],
		});
	};

	/* `Order` is renumbered on every change rather than carried: the API indexes
	   the array by position, so a gap in the numbering would put the stored order
	   and the array's own order into disagreement about which step is which. */
	const renumber = (steps: PublishChainTemplate["Steps"]) =>
		steps.map((step, i) => ({ ...step, Order: i }));

	const moveStep = (index: number, by: -1 | 1) => {
		if (!draft) return;
		const next = [...draft.Steps];
		const to = index + by;
		if (to < 0 || to >= next.length) return;
		[next[index], next[to]] = [next[to], next[index]];
		patch({ Steps: renumber(next) });
	};

	const removeStep = (index: number) => {
		if (!draft) return;
		patch({ Steps: renumber(draft.Steps.filter((_s, i) => i !== index)) });
	};

	const save = async () => {
		if (!draft) return;
		const code = toCode(draft.Code || draft.Name || "");
		if (!code) {
			toast.show({
				message: "Give the workflow a name.",
				severity: "danger",
				duration: 4000,
			});
			return;
		}
		if (draft.Steps.length === 0) {
			/* A chain with no steps would publish an event the moment it was
			   submitted, which is the one outcome sign-off exists to prevent. */
			toast.show({
				message: "A workflow needs at least one approver.",
				severity: "danger",
				duration: 4000,
			});
			return;
		}
		try {
			// Workflows govern events entered through Event Manager only, so no
			// calendar scope is kept: a stored one would route invisibly.
			const body = {
				...draft,
				Code: code,
				AppliesTo: { ...draft.AppliesTo, Calendars: [] },
			};
			if (isNew) await createChain(body);
			else await saveChain(body);
			setDraft(null);
			toast.show({
				message: "Workflow saved. It applies to the next submission.",
				severity: "success",
				duration: 4000,
			});
		} catch (error) {
			toast.show({
				message:
					refusalDetail(error) ??
					"Could not save the workflow. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		}
	};

	const remove = async (chain: PublishChainTemplate) => {
		if (chain.AppliesTo?.Default) {
			/* Something has to catch an event whose type names no chain. */
			toast.show({
				message: "The default workflow cannot be deleted.",
				severity: "danger",
				duration: 4000,
			});
			return;
		}
		if (
			!window.confirm(
				`Delete "${chain.Name || chain.Code}"? Events of its types will use the default workflow instead.`,
			)
		) {
			return;
		}
		try {
			await deleteChain(chain.Code);
			toast.show({
				message: "Workflow deleted.",
				severity: "success",
				duration: 3000,
			});
		} catch (error) {
			toast.show({
				message:
					refusalDetail(error) ??
					"Could not delete the workflow. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		}
	};

	if (chainsLoading) {
		return (
			<div className="em-approve-loading">
				<TypedLottieSpinner
					type="Searching"
					size={180}
					label="Loading workflows..."
				/>
			</div>
		);
	}

	return (
		<>
			{chainsError ? (
				<div className="em-picker-empty">
					The approval workflows could not be loaded.
				</div>
			) : (
				<>
					<div className="em-roles em-mt-sm">
						{chains.map((chain) => {
							const open = draft?.Code === chain.Code && !isNew;
							/* Both dimensions in the summary line, labelled, because
							   "Co-Curricular, Edumate" read as one list would suggest the
							   workflow governs either when it governs a co-curricular
							   event OR anything off the Edumate feed, which is two
							   different questions answered in the same breath. */
							const scope = [
								...(chain.AppliesTo?.EventTypes ?? []).map(
									(t) => typeLabel.get(t) ?? t,
								),
								/* No longer editable, but a scope stored before that still
								   routes events until the workflow is next saved, so it is
								   shown rather than left as an invisible rule. */
								...(chain.AppliesTo?.Calendars ?? []).map((c) => `from ${c}`),
							];
							return (
								<div key={chain.Code} className="em-role">
									<div className="em-role__head">
										<div>
											<span className="em-role__name">
												{chain.Name || chain.Code}
											</span>
											<span className="em-muted">
												{chain.AppliesTo?.Default
													? "Everything not named by another workflow"
													: scope.length > 0
														? scope.join(", ")
														: "Nothing named — this workflow is never chosen"}
											</span>
										</div>
										{chain.Steps.length === 0 && (
											<span className="em-role__warn">
												<TriangleAlert size={14} aria-hidden="true" /> No
												approvers
											</span>
										)}
									</div>

									{/* The event's timeline again, read-only: the card and the
									    editor and the event itself all draw a workflow the same
									    way, so what you pick here is what an organiser sees. */}
									<div className="em-chain em-chain--compact">
										{chain.Steps.map((step) => {
											const holders = roleHolders.get(step.RoleId) ?? [];
											const role =
												step.Name || roleName.get(step.RoleId) || step.RoleId;
											/* One description, two triggers: the faces and the words
											   beside them state the same fact, so hovering either
											   answers the same question. */
											const derived = derivedRoles.has(step.RoleId);
											const tipLabel = holders.length
												? `${role}: ${holders.length} ${holders.length === 1 ? "holder" : "holders"}`
												: derived
													? role
													: `${role}: nobody holds this role`;
											const tipDetails = holders.length ? (
												<>
													{holders.map((h) => (
														<span key={h._id}>
															{personName(h.DelegateName || h.Name) ||
																h.ContactId}
															{h.Title ? ` — ${h.Title}` : ""}
															{h.DelegateName ? " (delegate)" : ""}
														</span>
													))}
												</>
											) : undefined;
											return (
												<div
													key={`${step.RoleId}-${step.Order}`}
													className="em-chain__row is-none"
												>
													<span className="em-chain__rail" aria-hidden="true">
														<span className="em-chain__dot" />
													</span>
													{/* One face per person who could take the step. A role
													    held by three people is three people an event could
													    be waiting on, and this card is where an
													    administrator checks that the cover exists. */}
													{/* Everyone, on hover. The stack says how many and the
													    line names the first; the rest are a pointer away,
													    with the job each of them does — which is how an
													    administrator tells two Sandejases apart. */}
													<Tooltip
														label={tipLabel}
														details={tipDetails}
														align="start"
														className="em-chain__faces-wrap"
													>
														<span className="em-chain__faces">
															{holders.length === 0 ? (
																<CCAvatar name={role} size={28} decorative />
															) : (
																holders
																	.slice(0, MAX_FACES)
																	.map((h) => (
																		<CCAvatar
																			key={h._id}
																			src={imageUrl(
																				h.DelegateTo || h.ContactId,
																			)}
																			name={
																				personName(h.DelegateName || h.Name) ||
																				role
																			}
																			size={28}
																			decorative
																		/>
																	))
															)}
															{holders.length > MAX_FACES && (
																<span className="em-chain__faces-more">
																	+{holders.length - MAX_FACES}
																</span>
															)}
														</span>
													</Tooltip>
													{/* `interactive` here, unlike on the faces: it keeps the
													    wrapper out of `aria-hidden`, so the name and the role
													    stay in the accessibility tree. The faces are
													    decorative and carry the spoken label for both. */}
													<Tooltip
														label={tipLabel}
														details={tipDetails}
														align="start"
														interactive
														className="em-chain__body-wrap"
													>
														<span className="em-chain__body">
															<span className="em-chain__who">
																{holderLine(holders, role)}
															</span>
															<span
																className={`em-chain__role${holders.length || derived ? "" : " is-unheld"}`}
															>
																{holders.length || derived
																	? role
																	: "No one holds this role"}
															</span>
														</span>
													</Tooltip>
												</div>
											);
										})}
									</div>

									{/* Icons, since a card carries at most these two actions and
									    their glyphs are the ones every editor uses. Named for
									    anyone not reading by sight: `title` for the pointer, a
									    visually-hidden label for the accessibility tree. */}
									{!open && (
										<span className="em-chain-actions">
											<CCButton
												variant="icon"
												size="sm"
												title={`Edit ${chain.Name || chain.Code}`}
												onClick={() => edit(chain)}
											>
												<SquarePen size={16} aria-hidden="true" />
												<span className="em-sr-only">
													Edit {chain.Name || chain.Code}
												</span>
											</CCButton>
											{!chain.AppliesTo?.Default && (
												<CCButton
													variant="icon"
													colour="danger"
													size="sm"
													disabled={chainsSaving}
													title={`Delete ${chain.Name || chain.Code}`}
													onClick={() => void remove(chain)}
												>
													<Trash2 size={16} aria-hidden="true" />
													<span className="em-sr-only">
														Delete {chain.Name || chain.Code}
													</span>
												</CCButton>
											)}
										</span>
									)}
								</div>
							);
						})}
					</div>

					{!draft && (
						<CCButton
							variant="outline"
							size="sm"
							className="em-mt-sm"
							onClick={() => {
								setIsNew(true);
								setDraft(blankChain());
							}}
						>
							<Plus size={15} aria-hidden="true" /> Add workflow
						</CCButton>
					)}
				</>
			)}

			{/* A dialog, not a panel below the list.
			    
			    The editor is the whole task once it is open — a name, ten type
			    toggles, and a reorderable list of approvers — and inline it pushed
			    the workflow it was editing off the top of a long page, so the thing
			    being changed and the controls changing it were never on screen
			    together. CCModal traps focus and closes on Escape, which an inline
			    block gave nobody. */}
			{draft && (
				<CCModal
					open
					onClose={() => {
						setDraft(null);
						setIsNew(false);
					}}
					title={isNew ? "New workflow" : `Editing ${draft.Name || draft.Code}`}
					className="em-chain-modal"
					footer={
						<div className="em-modal-footer">
							<CCButton
								variant="outline"
								onClick={() => {
									setDraft(null);
									setIsNew(false);
								}}
							>
								Cancel
							</CCButton>
							<CCButton
								variant="solid"
								colour="primary"
								disabled={chainsSaving}
								onClick={() => void save()}
							>
								{chainsSaving ? (
									<CCSpinner size="inline" label="Saving..." />
								) : (
									"Save workflow"
								)}
							</CCButton>
						</div>
					}
				>
					<CCField
						id="em-chain-name"
						label="Name"
						fullWidth
						value={draft.Name ?? ""}
						onChange={(e) =>
							patch({
								Name: e.target.value,
								// The code follows the name until the chain exists; after
								// that it is the key events were submitted against and
								// must not move under them.
								Code: isNew ? toCode(e.target.value) : draft.Code,
							})
						}
					/>

					{!draft.AppliesTo?.Default && (
						<>
							{typesLoaded && typeOptions.length === 0 ? (
								/* Not an empty picker. The catalogue is seeded by a script
								   that has to be run before the API accepts any value here,
								   so nothing to show means an unseeded environment rather
								   than a school with no event types — and a blank space says
								   neither. */
								<div className="em-picker-empty">
									No event types are configured. Until the EventActivityTypes
									dictionary is seeded, a workflow can only be set as the
									default.
								</div>
							) : (
								<CCMultiSelect
									className="em-multi-below"
									label="Applies to event types"
									placeholder="Choose event types"
									options={typeOptions.map((type) => {
										const taken = claimedTypes.get(type);
										const on = (draft.AppliesTo.EventTypes ?? []).includes(
											type,
										);
										return {
											value: type,
											label: typeLabel.get(type) ?? type,
											detail: taken && !on ? `On ${taken}` : undefined,
											disabled: !!taken && !on,
										};
									})}
									values={draft.AppliesTo.EventTypes ?? []}
									onChange={setTypes}
								/>
							)}
						</>
					)}

					<div className="em-approve-section">Approvers, in order</div>
					{draft.Steps.length === 0 ? (
						<div className="em-picker-empty">
							No approvers yet. A chain with none would publish an event the
							moment it was submitted.
						</div>
					) : (
						/* The same timeline an event shows, so what is being built here
						   is recognisably the thing that will appear on the event: rail,
						   face, name over role. The one addition is who each step will
						   actually go to — a step whose role nobody holds is a workflow
						   that will refuse every submission, and this is the screen where
						   that can be fixed. */
						<div className="em-chain em-chain--editing">
							{draft.Steps.map((step, i) => {
								const holders = roleHolders.get(step.RoleId) ?? [];
								const role =
									roleName.get(step.RoleId) || step.Name || step.RoleId;
								const derived = derivedRoles.has(step.RoleId);
								return (
									<div
										key={`${step.RoleId}-${i}`}
										className="em-chain__row is-none"
									>
										<span className="em-chain__rail" aria-hidden="true">
											<span className="em-chain__dot" />
										</span>
										{/* Who holds the step. No number: the rail already runs
										    top to bottom in order, and the arrows beside each row
										    are what changes it — a numeral as well was a third
										    telling of the same thing.
										    
										    A role nobody holds shows an empty circle rather than a
										    face, which is the tell, and it lines up with the red
										    line beside it. */}
										<span className="em-chain__lead">
											{holders.length === 0 && (
												<span
													className="em-chain__facegap"
													aria-hidden="true"
												/>
											)}
											{holders.length > 0 && (
												<Tooltip
													label={`${role}: ${holders.length} ${holders.length === 1 ? "holder" : "holders"}`}
													details={
														<>
															{holders.map((h) => (
																<span key={h._id}>
																	{personName(h.DelegateName || h.Name) ||
																		h.ContactId}
																	{h.Title ? ` — ${h.Title}` : ""}
																	{h.DelegateName ? " (delegate)" : ""}
																</span>
															))}
														</>
													}
													align="start"
													className="em-chain__faces-wrap"
												>
													<span className="em-chain__faces">
														{holders.slice(0, MAX_FACES).map((h) => (
															<CCAvatar
																key={h._id}
																src={imageUrl(h.DelegateTo || h.ContactId)}
																name={
																	personName(h.DelegateName || h.Name) || role
																}
																size={28}
																decorative
															/>
														))}
														{holders.length > MAX_FACES && (
															<span className="em-chain__faces-more">
																+{holders.length - MAX_FACES}
															</span>
														)}
													</span>
												</Tooltip>
											)}
										</span>
										<span className="em-chain__body">
											<span className="em-chain__who">
												{holderLine(holders, role)}
											</span>
											<span
												className={`em-chain__role${holders.length || derived ? "" : " is-unheld"}`}
											>
												{holders.length || derived
													? role
													: "No one holds this role"}
											</span>
										</span>
										<span className="em-chain__meta em-chain__meta--controls">
											<CCButton
												variant="icon"
												size="sm"
												disabled={i === 0}
												title="Move up"
												onClick={() => moveStep(i, -1)}
											>
												<ArrowUp size={15} aria-hidden="true" />
												<span className="em-sr-only">Move {role} up</span>
											</CCButton>
											<CCButton
												variant="icon"
												size="sm"
												disabled={i === draft.Steps.length - 1}
												title="Move down"
												onClick={() => moveStep(i, 1)}
											>
												<ArrowDown size={15} aria-hidden="true" />
												<span className="em-sr-only">Move {role} down</span>
											</CCButton>
											<CCButton
												variant="icon"
												colour="danger"
												size="sm"
												title="Remove"
												onClick={() => removeStep(i)}
											>
												<X size={15} aria-hidden="true" />
												<span className="em-sr-only">Remove {role}</span>
											</CCButton>
										</span>
									</div>
								);
							})}
						</div>
					)}

					<div className="em-chain-add">
						{roles.map((role) => (
							<CCButton
								key={role.Code}
								variant="outline"
								size="sm"
								onClick={() => addStep(role.Code)}
							>
								<Plus size={14} aria-hidden="true" /> {role.Name || role.Code}
							</CCButton>
						))}
					</div>

					{/* Said before saving, not after: this is somebody's approval
							    being undone. */}
					<p className="em-muted em-mt-sm">
						Saving applies to the next submission. Any event already in review
						on this workflow goes back to the organiser to be submitted again.
					</p>
				</CCModal>
			)}
		</>
	);
};
